import { ChangeEvent, MouseEvent } from "react";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { NoteType } from "../../types/credit-debit-note.type";
import { SaleRoute } from "../../types/sale-route.type";
import { Branch } from "../../types/user.type";
import { NOTE_TYPE_ACTION_LABEL } from "./constants";
import { NoteDraft, NoteFormErrors } from "./noteDraft";

interface NoteDocumentSectionProps {
  draft: NoteDraft;
  errors: NoteFormErrors;
  canSelectBranch: boolean;
  branches: Branch[];
  routes: SaleRoute[];
  onChange: (patch: Partial<NoteDraft>) => void;
  onChangeBranch: (branchUuid: string) => void;
}

const NOTE_TYPES: NoteType[] = ["CREDIT", "DEBIT"];

/** Which document, on which day, for which branch and route — the note number's inputs. */
export default function NoteDocumentSection({
  draft,
  errors,
  canSelectBranch,
  branches,
  routes,
  onChange,
  onChangeBranch,
}: Readonly<NoteDocumentSectionProps>) {
  const handleNoteTypeChange = (_event: MouseEvent<HTMLElement>, value: NoteType | null) => {
    if (value) onChange({ noteType: value });
  };
  const handleNoteDateChange = (value: Dayjs | null) => onChange({ noteDate: value });
  const handleBranchChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChangeBranch(event.target.value);
  const handleRouteChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ routeCode: event.target.value });

  const isRouteBlocked = canSelectBranch && !draft.branchUuid;
  const routeHelperText = errors.route || (isRouteBlocked ? "เลือกสาขาก่อน" : "ใช้ออกเลขที่เอกสาร");

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <ToggleButtonGroup
          exclusive
          color="primary"
          size="small"
          value={draft.noteType}
          onChange={handleNoteTypeChange}
        >
          {NOTE_TYPES.map((noteType) => (
            <ToggleButton key={noteType} value={noteType} sx={{ px: 3 }}>
              {NOTE_TYPE_ACTION_LABEL[noteType]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Grid>
      <Grid size={{ xs: 12, sm: canSelectBranch ? 4 : 6 }}>
        <DatePicker
          label="วันที่เอกสาร"
          value={draft.noteDate}
          format="DD/MM/YYYY"
          maxDate={dayjs()}
          onChange={handleNoteDateChange}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              error: Boolean(errors.noteDate),
              helperText: errors.noteDate,
            },
          }}
        />
      </Grid>
      {canSelectBranch && (
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="สาขา"
            value={draft.branchUuid}
            onChange={handleBranchChange}
            error={Boolean(errors.branch)}
            helperText={errors.branch}
          >
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.uuid}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      )}
      <Grid size={{ xs: 12, sm: canSelectBranch ? 4 : 6 }}>
        <TextField
          select
          fullWidth
          size="small"
          label="สายส่ง"
          value={draft.routeCode}
          disabled={isRouteBlocked}
          onChange={handleRouteChange}
          error={Boolean(errors.route)}
          helperText={routeHelperText}
        >
          {routes.map((route) => (
            <MenuItem key={route.uuid} value={route.route_code}>
              {route.route_code} - {route.route_name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );
}
